import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Image as ImageIcon, Upload, Trash2, Star, Camera, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { inventoryService } from '@/services/inventory';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { ItemImage } from '@/types/inventory';
import { IMAGE_MAX_BYTES, IMAGE_MAX_LABEL, imageTooLargeMessage } from './imageProfile';

interface Props {
  itemId: string | null;
}

const MAX_FILES = 15;
const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

/**
 * Item images gallery — list, upload (drag-drop or click), delete, and two
 * independent designations:
 *  - Primary (sortOrder=0) — the gallery lead / detail-carousel order.
 *  - Packshot (isPackshot) — the storefront LISTING photo, decoupled from
 *    gallery order; the portal falls back to Primary when none is tagged.
 * Up to 15 images per item, IMAGE_MAX_BYTES each (see imageProfile.ts). Backend stores via the configured
 * FileStorageService (local FS / S3).
 */
export function ItemImagesTab({ itemId }: Props) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const imagesQuery = useQuery({
    queryKey: ['inventory', 'images', itemId],
    queryFn: () => inventoryService.listImages(itemId!),
    enabled: !!itemId,
  });

  const upload = useMutation({
    mutationFn: (file: File) => inventoryService.uploadImage(itemId!, file),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory', 'images', itemId] });
      qc.invalidateQueries({ queryKey: ['inventory', 'items'] });
      toast.success('Image uploaded');
    },
    onError: (err: unknown) => {
      // Translate the BE's PayloadTooLarge into a friendly, action-led
      // message rather than NestJS's raw "File too large" / 413 string.
      const e = err as { status?: number; message?: string };
      const tooLarge =
        e?.status === 413 ||
        (typeof e?.message === 'string' && /too large|payload/i.test(e.message));
      if (tooLarge) {
        toast.error(imageTooLargeMessage());
        return;
      }
      toast.error(err instanceof Error ? err.message : 'Upload failed');
    },
  });

  const remove = useMutation({
    mutationFn: (imageId: string) => inventoryService.deleteImage(itemId!, imageId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory', 'images', itemId] });
      qc.invalidateQueries({ queryKey: ['inventory', 'items'] });
      toast.success('Image removed');
    },
  });

  const reorder = useMutation({
    mutationFn: (ids: string[]) => inventoryService.reorderImages(itemId!, ids),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory', 'images', itemId] });
      qc.invalidateQueries({ queryKey: ['inventory', 'items'] });
    },
  });

  const packshot = useMutation({
    mutationFn: ({ imageId, on }: { imageId: string; on: boolean }) =>
      inventoryService.setPackshot(itemId!, imageId, on),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['inventory', 'images', itemId] });
      qc.invalidateQueries({ queryKey: ['inventory', 'items'] });
      toast.success(vars.on ? 'Packshot set' : 'Packshot cleared');
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not update packshot'),
  });

  const images = imagesQuery.data ?? [];
  const atLimit = images.length >= MAX_FILES;

  const handleFiles = async (files: FileList | null | undefined) => {
    if (!files || !itemId) return;
    const arr = Array.from(files);
    for (const f of arr) {
      if (images.length >= MAX_FILES) {
        toast.error(`Limit reached (${MAX_FILES} images per item)`);
        break;
      }
      if (!ACCEPTED.includes(f.type)) {
        toast.error(`${f.name}: type ${f.type || 'unknown'} not supported`);
        continue;
      }
      if (f.size > IMAGE_MAX_BYTES) {
        toast.error(imageTooLargeMessage(f.name));
        continue;
      }
      await upload.mutateAsync(f);
    }
  };

  const setPrimary = (img: ItemImage) => {
    const others = images.filter((i) => i.id !== img.id);
    reorder.mutate([img.id, ...others.map((i) => i.id)]);
  };

  if (!itemId) return null;

  return (
    <div className="space-y-4">
      {/* Drag-drop upload zone */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (atLimit) {
            toast.error(`Limit reached (${MAX_FILES} images per item)`);
            return;
          }
          handleFiles(e.dataTransfer.files);
        }}
        className={cn(
          'rounded-md border-2 border-dashed p-6 text-center transition-colors',
          dragOver
            ? 'border-primary bg-primary/5'
            : atLimit
              ? 'border-border bg-muted/30 opacity-60'
              : 'border-border bg-muted/30 hover:border-primary/50',
        )}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED.join(',')}
          multiple
          className="hidden"
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
        <Upload className="mx-auto h-5 w-5 text-muted-foreground" />
        <div className="mt-2 text-[13px] text-foreground">
          {atLimit ? `Limit reached (${MAX_FILES} images)` : 'Drag photos here, or'}{' '}
          {!atLimit && (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="font-medium text-primary underline-offset-2 hover:underline"
            >
              choose files
            </button>
          )}
        </div>
        <div className="mt-1 text-[11px] text-muted-foreground">
          JPEG, PNG, WebP, GIF · {IMAGE_MAX_LABEL} per image · {images.length} of {MAX_FILES} images used
        </div>
      </div>

      {/* Legend — clarify the two designations for the operator */}
      {images.length > 0 && (
        <p className="text-[11px] leading-relaxed text-muted-foreground">
          <span className="font-medium text-emerald-600">Packshot</span> = the photo
          shown on your storefront listing (falls back to Primary if none is tagged).{' '}
          <span className="font-medium text-primary">Primary</span> = the first image in
          the gallery / product-detail carousel.
        </p>
      )}

      {/* Gallery */}
      {imagesQuery.isLoading ? (
        <div className="py-6 text-center text-sm text-muted-foreground">Loading images…</div>
      ) : images.length === 0 ? (
        <div className="py-6 text-center">
          <ImageIcon className="mx-auto h-5 w-5 text-muted-foreground/60" />
          <div className="mt-2 text-[12.5px] text-muted-foreground">
            No images yet. Drag a few in to get started.
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {images.map((img, idx) => (
            <div
              key={img.id}
              className={cn(
                'group relative overflow-hidden rounded-md border bg-card',
                img.isPackshot
                  ? 'border-emerald-500 ring-2 ring-emerald-500'
                  : idx === 0
                    ? 'border-primary/40 ring-1 ring-primary/40'
                    : 'border-border/70',
              )}
            >
              <div className="aspect-square w-full overflow-hidden bg-muted">
                {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
                <img
                  src={img.thumbnailUrl ?? img.url}
                  alt={img.filename}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
                {/* Packshot — persistent emerald overlay so the picked image
                    is unmistakable even when not hovered. */}
                {img.isPackshot && (
                  <div className="pointer-events-none absolute inset-0 ring-2 ring-inset ring-emerald-500/70" />
                )}
              </div>
              {/* Packshot badge — the storefront listing photo (top-left) */}
              {img.isPackshot && (
                <div className="absolute left-1.5 top-1.5 inline-flex items-center gap-1 rounded-full bg-emerald-600 px-2 py-0.5 text-[10.5px] font-semibold text-white shadow-md ring-1 ring-white/40">
                  <CheckCircle2 className="h-3 w-3" />
                  Packshot
                </div>
              )}
              {/* Primary — gallery lead / carousel order (top-right) */}
              {idx === 0 && (
                <div className="absolute right-1.5 top-1.5 inline-flex items-center gap-1 rounded-full bg-primary/90 px-1.5 py-0.5 text-[10px] font-medium text-primary-foreground">
                  <Star className="h-2.5 w-2.5" />
                  Primary
                </div>
              )}
              <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-1 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5 opacity-0 transition-opacity group-hover:opacity-100">
                <div className="text-[10px] text-white/85">{fmt.compact(img.size)}B</div>
                <div className="flex items-center gap-1">
                  {/* Set / unset packshot (storefront listing photo) */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="icon"
                        variant="ghost"
                        className={cn(
                          'h-6 w-6 hover:bg-white/20 hover:text-white',
                          img.isPackshot ? 'text-emerald-300' : 'text-white/85',
                        )}
                        onClick={() =>
                          packshot.mutate({ imageId: img.id, on: !img.isPackshot })
                        }
                        disabled={packshot.isPending}
                      >
                        <Camera className="h-3 w-3" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {img.isPackshot ? 'Unset packshot' : 'Set as packshot (storefront listing)'}
                    </TooltipContent>
                  </Tooltip>
                  {/* Move to front — gallery / carousel order */}
                  {idx !== 0 && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-6 w-6 text-white/85 hover:bg-white/20 hover:text-white"
                          onClick={() => setPrimary(img)}
                        >
                          <Star className="h-3 w-3" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Move to front (gallery order)</TooltipContent>
                    </Tooltip>
                  )}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6 text-white/85 hover:bg-destructive hover:text-white"
                        onClick={() => remove.mutate(img.id)}
                        disabled={remove.isPending}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Delete</TooltipContent>
                  </Tooltip>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
